import { Box, Drawer, Stack, type Theme, useMediaQuery } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import MapComponent from '/@/components/map';
import { useFieldTrials, useFieldTrialsTypes } from '/@/hooks/field-trial-network';
import { useMyFields } from '/@/hooks/fields';
import { useAllUserNodesLookup, useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import DesktopSidebar, { SIDEBAR_WIDTH } from './desktop-sidebar';
import MobileFilterBar from './mobile-filter-bar';
import MobileFiltersDrawer from './mobile-filters-drawer';
import { PHENOLOGY_OPTIONS } from './phenology-options';
import RenderMarkerDescription, { type Marker } from './trial-description';

const ZOOM_VALUES = { initial: 4, zoomed: 16 } as const;

type MapHandle = {
    focusMap: () => void;
    zoomMarker: (coords: { lat: number; lng: number }, zoom: number) => void;
    infoWindowRef: { current: google.maps.InfoWindow | null };
};

// Empty selection means "no filter applied" (show all), so a group only counts as
// active once at least one option is picked.
const countActiveFilters = (selected: unknown[]) => (selected.length > 0 ? 1 : 0);

const FieldTrialNetwork = () => {
    // Match app-navbar: BottomBar below `sm`, AppSidebar from `sm` up
    const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const country = useSelector(getSessionCountry);
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,geometry,name,area,userNode',
    });
    const { allNodesById } = useAllUserNodesLookup();
    const { data: ownUserNode } = useOwnUserNode();

    const parsedFields = useMemo(
        () =>
            (fieldsQuery.data?.results || []).map((field) => ({
                ...field,
                _id: field.id,
                markerType: 'field',
                name: field.name,
                area: field.area,
                growerName:
                    field.userNode === ownUserNode?.id ? _('Mis lotes') : allNodesById.get(field.userNode)?.name,
            })),
        [fieldsQuery.data, allNodesById, ownUserNode?.id]
    );

    const fieldTrialsQuery = useFieldTrials({ page: 1, pageSize: 10000 });

    const { data: trialTypesOptions } = useFieldTrialsTypes({ page: 1, pageSize: 100 });

    const trialTypeToggleOptions = useMemo(
        () => (trialTypesOptions ?? []).map((t) => ({ value: t.id, label: _(t.name) })),
        [trialTypesOptions]
    );

    const phenologyToggleOptions = useMemo(
        () =>
            PHENOLOGY_OPTIONS.map((option) => ({
                value: option.value,
                label: _(option.label),
            })),
        []
    );

    const campaignOptions = useMemo(() => {
        const options = (fieldTrialsQuery.data?.results || []).map((ft) => ({
            value: ft.campaign,
            label: ft.campaign,
        }));
        const map = new Map(options.map((option) => [option.label, option]));
        const uniquesOptions = [...map.values()] as { value: string; label: string }[];
        uniquesOptions.sort((a, b) => {
            return Number(String(a.value).substring(0, 4)) - Number(String(b.value).substring(0, 4));
        });
        return uniquesOptions;
    }, [fieldTrialsQuery.data?.results]);

    const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<string[]>([]);
    const [selectedPhenologies, setSelectedPhenologies] = useState<string[]>([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [selectedMarker, setSelectedMarker] = useState<Marker | null>(null);
    const [includeFields, setIncludeFields] = useState(true);

    const offline = useSelector((state: { network: { offline: boolean } }) => state.network.offline);

    const filteredTrials = useMemo(() => {
        const results = fieldTrialsQuery?.data?.results || [];
        const typeSet = selectedTypes.length ? new Set(selectedTypes) : null;
        const phenologySet = selectedPhenologies.length ? new Set(selectedPhenologies) : null;
        const campaignSet = selectedCampaign.length ? new Set(selectedCampaign) : null;

        return results
            .filter((trial) => {
                if (typeSet && !typeSet.has(trial.type.id)) return false;
                if (phenologySet && !phenologySet.has(trial.phenologicalStage)) return false;
                if (campaignSet && !campaignSet.has(trial.campaign)) return false;
                return true;
            })
            .map((trial) => ({
                ...trial,
                lat: trial.latitude,
                lng: trial.longitude,
            }));
    }, [fieldTrialsQuery.data?.results, selectedTypes, selectedPhenologies, selectedCampaign]);

    const map = useRef<MapHandle | null>(null);

    const closeDescription = useCallback(() => {
        if (!map.current) return;
        map.current.focusMap();
        setSelectedMarker(null);
        map.current.infoWindowRef.current?.close();
    }, []);

    const setMapRef = useCallback((ref: MapHandle | null) => {
        map.current = ref;
    }, []);

    const handleTrialTypeChange = useCallback((types: (string | number)[]) => {
        setSelectedTypes(types);
        setSelectedMarker(null);
    }, []);

    const handleTrialPhenologyChange = useCallback((phenologies: string[]) => {
        setSelectedPhenologies(phenologies);
        setSelectedMarker(null);
    }, []);

    const handleMarkerClick = useCallback((marker: Marker) => {
        if (!map.current) return;
        const coordinates = {
            lat: (marker.latitude || marker.lat) as number,
            lng: (marker.longitude || marker.lng) as number,
        };
        map.current.zoomMarker(coordinates, ZOOM_VALUES.zoomed);
        // Only open the description drawer for trials, not for fields.
        if (marker.markerType === 'field') return;
        setSelectedMarker(marker);
        setFiltersOpen(false);
    }, []);

    const handleCampaignChange = useCallback((campaign: string[]) => {
        setSelectedCampaign(campaign);
        setSelectedMarker(null);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSelectedTypes([]);
        setSelectedPhenologies([]);
        setSelectedCampaign([]);
        setSelectedMarker(null);
    }, []);

    const handleOpenFilters = useCallback(() => {
        setFiltersOpen(true);
        setSelectedMarker(null);
    }, []);

    const handleCloseFilters = useCallback(() => setFiltersOpen(false), []);

    const handleApplyFilters = useCallback(
        ({
            types,
            phenologies,
            campaigns,
        }: {
            types: (string | number)[];
            phenologies: string[];
            campaigns: string[];
        }) => {
            setSelectedTypes(types);
            setSelectedPhenologies(phenologies);
            setSelectedCampaign(campaigns);
            setSelectedMarker(null);
        },
        []
    );

    const activeFilterCount = useMemo(() => {
        return (
            countActiveFilters(selectedTypes) +
            countActiveFilters(selectedPhenologies) +
            countActiveFilters(selectedCampaign)
        );
    }, [selectedTypes, selectedPhenologies, selectedCampaign]);

    const displayFilterBar = isMobileView && !selectedMarker;

    const fields = useMemo(() => {
        return includeFields ? filteredTrials.concat(parsedFields) : filteredTrials;
    }, [includeFields, filteredTrials, parsedFields]);

    useEffect(() => {
        if (!map.current || fields.length === 0) return;
        map.current.focusMap();
    }, [fields]);

    const description = selectedMarker ? (
        <RenderMarkerDescription
            marker={selectedMarker}
            onClose={closeDescription}
            phenologiesOptions={PHENOLOGY_OPTIONS}
            isMobileView={isMobileView}
        />
    ) : null;

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            width='100%'
            minHeight={0}
            // Mobile: fill #app-content above the fixed BottomBar (do not change navbar)
            position={{ xs: 'absolute', sm: 'relative' }}
            top={{ xs: 0, sm: 'auto' }}
            left={{ xs: 0, sm: 'auto' }}
            right={{ xs: 0, sm: 'auto' }}
            bottom={{ xs: 0, sm: 'auto' }}
            height={{ xs: 'auto', sm: '100%' }}
        >
            {!isMobileView && (
                <DesktopSidebar
                    trialTypesOptions={trialTypesOptions}
                    trialTypeToggleOptions={trialTypeToggleOptions}
                    selectedTypes={selectedTypes}
                    onTrialTypeChange={handleTrialTypeChange}
                    selectedPhenologies={selectedPhenologies}
                    onPhenologyChange={handleTrialPhenologyChange}
                    campaignOptions={campaignOptions}
                    selectedCampaign={selectedCampaign}
                    onCampaignChange={handleCampaignChange}
                    includeFields={includeFields}
                    onIncludeFieldsChange={setIncludeFields}
                    onClearFilters={handleClearFilters}
                    hasActiveFilters={activeFilterCount > 0}
                />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} width='100%' flex='1 1 0' minHeight={0}>
                <Box flex='1 1 0' position='relative' minHeight={0} width='100%'>
                    <MapComponent
                        ref={setMapRef}
                        fields={fields}
                        onFieldClick={handleMarkerClick}
                        offline={offline}
                        simplifyPolygons
                        isLoading={fieldTrialsQuery.isValidating || fieldsQuery.isValidating}
                    />
                </Box>

                {displayFilterBar && (
                    <MobileFilterBar
                        activeFilterCount={activeFilterCount}
                        includeFields={includeFields}
                        onFilterClick={handleOpenFilters}
                        onIncludeFieldsChange={setIncludeFields}
                    />
                )}

                {!isMobileView && selectedMarker && (
                    <Stack
                        flexShrink={0}
                        zIndex={20}
                        bgcolor='background.paper'
                        width={SIDEBAR_WIDTH}
                        height='100%'
                        overflow='auto'
                    >
                        {description}
                    </Stack>
                )}
            </Stack>

            {isMobileView && (
                <>
                    <MobileFiltersDrawer
                        open={filtersOpen}
                        onClose={handleCloseFilters}
                        onApply={handleApplyFilters}
                        trialTypesLoading={!trialTypesOptions}
                        trialTypeOptions={trialTypeToggleOptions}
                        phenologyOptions={phenologyToggleOptions}
                        campaignOptions={campaignOptions}
                        selectedTypes={selectedTypes}
                        selectedPhenologies={selectedPhenologies}
                        selectedCampaign={selectedCampaign}
                    />
                    <Drawer
                        anchor='bottom'
                        open={!!selectedMarker}
                        onClose={closeDescription}
                        hideBackdrop
                        PaperProps={{
                            sx: {
                                // Pin both edges: `100vh` is the large viewport on mobile browsers
                                // (URL bar retracted), so a vh-based height pushes the header off-screen.
                                top: 0,
                                bottom: BOTTOM_BAR_HEIGHT,
                                height: 'auto',
                                maxHeight: 'none',
                                borderRadius: 0,
                                overflow: 'unset',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {description}
                    </Drawer>
                </>
            )}
        </Stack>
    );
};

export default memo(FieldTrialNetwork);
